/**
 * SettingsView - Settings Module
 *
 * Comprehensive user settings including profile, security, integrations, and preferences.
 * Uses route segments: /settings/personal, /settings/integrations, etc.
 * Sidebar is managed by AppShell based on user role.
 */

import { useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SettingsPage } from '../components/SettingsPage';

type SettingsTab = 'personal' | 'security' | 'company' | 'integrations' | 'subscription' | 'imports' | 'support' | 'accounts';

const VALID_TABS: SettingsTab[] = ['personal', 'security', 'company', 'integrations', 'subscription', 'imports', 'support', 'accounts'];

export function SettingsView() {
  const navigate = useNavigate();
  const { section } = useParams<{ section?: string }>();

  // Get active tab from URL, default to 'personal'
  const activeTab: SettingsTab = VALID_TABS.includes(section as SettingsTab)
    ? (section as SettingsTab)
    : 'personal';

  // Redirect to /settings/personal if on bare /settings or invalid section
  useEffect(() => {
    if (!section || !VALID_TABS.includes(section as SettingsTab)) {
      navigate('/settings/personal', { replace: true });
    }
  }, [section, navigate]);

  // Update URL when tab changes
  const handleTabChange = useCallback((tab: SettingsTab) => {
    navigate(`/settings/${tab}`);
  }, [navigate]);

  // Note: Sidebar is managed by AppShell.tsx via SETTINGS_SECTIONS
  // which is dynamically filtered based on user role (isOwner)

  return <SettingsPage activeTab={activeTab} onTabChange={handleTabChange} />;
}
